/* eslint-disable */
/**
 * OsOversigt2Panel.jsx — OS Oversigt (erstatter LegacyOsOversigt)
 *
 * Datakilder (verificerede endpoints):
 *   GET /api/os/statistik          → { opgaver:{KOERER,FEJLET,FULDFOERT,...},
 *                                       afventende_beslutninger, fejl_24h,
 *                                       aktive_sager, os_opgaver_7d, kritiske_fejl_24h }
 *   GET /api/os/opgaver?status=KOERER&limit=20
 *   GET /api/os/opgaver?status=FEJLET&limit=20
 *   GET /api/os/log?niveau=FEJL&limit=50
 *   GET /api/os/tilstand           → { tilstand: [{noegel, vaerdi, opdateret_kl}] }
 *   GET /api/os/hjerne/kommunikation?limit=20 → { kommunikation: [...] }
 *
 * Design: Sager-stil, Sektioner med SammenklappeligSektion, mobil-kortliste.
 * Mobil-først: Android 360px viewport.
 */

import React, { useState, useCallback } from 'react';
import { Brain, AlertCircle, CheckCircle2, Activity, Clock, BarChart3, ShieldCheck } from 'lucide-react';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  DataTabel,
  TidslinjeListe,
  SektionsHeader,
  SammenklappeligSektion,
  useApiPolling,
  useWindowWidth,
  BG, KANT, TEKST, STATUS, RADIUS, SPACING, TYPE, FONT,
} from '../komponenter/index';

// ─── Hjælpefunktioner ────────────────────────────────────────────────────────

function formatTid(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('da-DK', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function niveauFarve(niveau) {
  const map = {
    KRITISK: STATUS.fejl,
    FEJL:    STATUS.fejl,
    ADVARSEL: STATUS.opmærksomhed,
    INFO:    STATUS.konfigureret,
    DEBUG:   STATUS.ikkeForbundet,
  };
  return map[niveau] || TEKST.sekundær;
}

// ─── Mobil: opgave-kortliste ──────────────────────────────────────────────────

function OpgaveKortListe({ opgaver, titel }) {
  if (!opgaver || opgaver.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {opgaver.map((o) => {
        const erFejl = o.status === 'FEJLET';
        const kantFarve = erFejl ? STATUS.fejl : STATUS.ai;
        return (
          <div
            key={o.id}
            style={{
              background: BG.kort,
              border: `1px solid ${KANT.standard}`,
              borderLeft: `3px solid ${kantFarve}`,
              borderRadius: RADIUS.kort,
              padding: '10px 12px',
              fontFamily: FONT.family,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                background: erFejl ? 'rgba(239,68,68,0.12)' : 'rgba(139,92,246,0.12)',
                border: `1px solid ${erFejl ? 'rgba(239,68,68,0.3)' : 'rgba(139,92,246,0.3)'}`,
                color: erFejl ? STATUS.fejl : STATUS.ai,
                borderRadius: RADIUS.badge, padding: '2px 6px',
              }}>
                {o.afdeling || '?'}
              </span>
              <span style={{ fontSize: 11, color: TEKST.sekundær, marginLeft: 'auto' }}>
                {formatTid(o.oprettet_kl)}
              </span>
            </div>
            <div style={{ fontSize: 13, color: TEKST.primær, fontWeight: 500, lineHeight: 1.4 }}>
              {o.titel || 'Ukendt opgave'}
            </div>
            {erFejl && o.fejlbesked && (
              <div style={{ fontSize: 11, color: STATUS.fejl, marginTop: 4, lineHeight: 1.4 }}>
                {o.fejlbesked.slice(0, 120)}{o.fejlbesked.length > 120 ? '…' : ''}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Mobil: tilstands-kortliste ───────────────────────────────────────────────

// OS-14: Serialisér objekt-værdier pænt — undgå [object Object]
function formatTilstandVaerdi(vaerdi) {
  if (vaerdi === null || vaerdi === undefined) return '—';
  if (typeof vaerdi === 'object') {
    // Hvis det er et cache-objekt med opdateret_kl eller sidst_opdateret, vis kompakt
    if (vaerdi.opdateret_kl || vaerdi.sidst_opdateret) {
      const ts = vaerdi.opdateret_kl || vaerdi.sidst_opdateret;
      const dato = new Date(ts).toLocaleString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
      return `cache (opdateret ${dato})`;
    }
    return JSON.stringify(vaerdi).slice(0, 120);
  }
  return String(vaerdi);
}

function TilstandKortListe({ tilstand }) {
  if (!tilstand || tilstand.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {tilstand.map((t) => {
        const erLaast = t.vaerdi === true || t.vaerdi === 'true';
        const erLaastNavn = t.noegel?.includes('laast');
        const vaerdiTekst = formatTilstandVaerdi(t.vaerdi);
        return (
          <div
            key={t.noegel}
            style={{
              // OS-13: column-layout med gap i stedet for space-between på mobil
              // sikrer at lange værdier wrappes til ny linje frem for off-screen.
              display: 'flex', flexDirection: 'column', gap: 3,
              background: BG.kort,
              border: `1px solid ${KANT.fin}`,
              borderRadius: RADIUS.badge,
              padding: '8px 12px',
              fontFamily: FONT.family,
            }}
          >
            <span style={{
              fontSize: 11, color: TEKST.sekundær,
              ...TYPE.mono,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {t.noegel}
            </span>
            <span style={{
              fontSize: 12, fontWeight: 700,
              color: erLaast && erLaastNavn ? STATUS.fejl : erLaast ? STATUS.live : TEKST.sekundær,
              // OS-13: word-break sikrer at lange tokens (fx JWT/Base64-fragmenter) ikke overflow
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
              title={vaerdiTekst}
            >
              {vaerdiTekst}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Hoved-komponent ─────────────────────────────────────────────────────────

export default function OsOversigt2Panel() {
  const bredde = useWindowWidth();
  const isMobil = bredde < 600;

  // Data polling — separate hooks med individuelle intervaller
  const { data: stat }       = useApiPolling('/os/statistik',                         30000);
  const { data: koerer }     = useApiPolling('/os/opgaver?status=KOERER&limit=20',    20000);
  const { data: fejlet }     = useApiPolling('/os/opgaver?status=FEJLET&limit=20',    30000);
  const { data: fejllog }    = useApiPolling('/os/log?niveau=FEJL&limit=50',          60000);
  const { data: tilstand }   = useApiPolling('/os/tilstand',                          60000);
  const { data: agentkomm }  = useApiPolling('/os/hjerne/kommunikation?limit=20',     30000);

  // Udpak data
  const opgaverKoerer   = koerer?.opgaver || [];
  const opgaverFejlet   = fejlet?.opgaver || [];
  const logLinjer       = fejllog?.log || [];
  const tilstandListe   = tilstand?.tilstand || [];
  const agentAktivitet  = agentkomm?.kommunikation || [];

  // KPI-beregninger
  const igangvaerende   = stat?.opgaver?.KOERER || 0;
  const fejlet24h       = stat?.fejl_24h || 0;
  const fuldfoert7d     = stat?.os_opgaver_7d || 0;
  const afventer        = stat?.afventende_beslutninger || 0;
  const systemLaast     = tilstandListe.find(t => t.noegel === 'system_laast');
  const erLaast         = systemLaast?.vaerdi === true || systemLaast?.vaerdi === 'true';

  // Kolonner: kørende/fejlede opgaver
  const opgaveKolonner = [
    {
      nøgle: 'afdeling',
      label: 'Afdeling',
      render: (v) => (
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
          background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)',
          color: STATUS.ai, borderRadius: RADIUS.badge, padding: '2px 6px',
        }}>
          {v || '?'}
        </span>
      ),
    },
    {
      nøgle: 'titel',
      label: 'Opgave',
      render: (v) => (
        <span style={{ ...TYPE.tabelCelle, color: TEKST.primær }}>
          {v || '—'}
        </span>
      ),
    },
    {
      nøgle: 'status',
      label: 'Status',
      render: (v) => (
        <StatusBadge
          status={v === 'FEJLET' ? 'FEJL' : v === 'KOERER' ? 'AI' : 'KONFIGURERET'}
          label={v}
        />
      ),
    },
    {
      nøgle: 'oprettet_kl',
      label: 'Oprettet',
      render: (v) => (
        <span style={{ fontSize: 12, color: TEKST.sekundær }}>{formatTid(v)}</span>
      ),
    },
  ];

  // Kolonner: fejllog
  const fejlKolonner = [
    {
      nøgle: 'niveau',
      label: 'Niveau',
      render: (v) => (
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
          color: niveauFarve(v),
          background: `${niveauFarve(v)}18`,
          border: `1px solid ${niveauFarve(v)}40`,
          borderRadius: RADIUS.badge, padding: '2px 6px',
        }}>
          {v}
        </span>
      ),
    },
    {
      nøgle: 'besked',
      label: 'Besked',
      render: (v) => (
        <span style={{ fontSize: 12, color: TEKST.sekundær, wordBreak: 'break-word' }}>
          {(v || '—').slice(0, 200)}{v && v.length > 200 ? '…' : ''}
        </span>
      ),
    },
    {
      nøgle: 'oprettet_kl',
      label: 'Tid',
      render: (v) => (
        <span style={{ fontSize: 11, color: TEKST.sekundær, whiteSpace: 'nowrap' }}>{formatTid(v)}</span>
      ),
    },
  ];

  // Tilstand-kolonner (desktop)
  const tilstandKolonner = [
    {
      nøgle: 'noegel',
      label: 'Nøgle',
      // OS-13: minWidth+maxWidth begrænsning forhindrer nøgle-kolonnen i at
      // strække tabellen horisontalt ved lange nøglenavne.
      bredde: '35%',
      render: (v) => (
        <span style={{
          ...TYPE.mono,
          color: TEKST.primær,
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 220,
        }}>{v}</span>
      ),
    },
    {
      nøgle: 'vaerdi',
      label: 'Værdi',
      bredde: '45%',
      render: (v, row) => {
        const erLaastVal = v === true || v === 'true';
        const erLaastKey = row?.noegel?.includes('laast');
        // OS-13: lange værdier (fx JSON-strings) får wordBreak+ellipsis.
        const formateret = formatTilstandVaerdi(v);
        return (
          <span style={{
            fontSize: 12, fontWeight: 600,
            color: erLaastVal && erLaastKey ? STATUS.fejl : erLaastVal ? STATUS.live : TEKST.sekundær,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 300,
            title: formateret, // full tekst ved hover
          }}>
            {formateret}
          </span>
        );
      },
    },
    {
      nøgle: 'opdateret_kl',
      label: 'Opdateret',
      bredde: '20%',
      render: (v) => (
        <span style={{ fontSize: 11, color: TEKST.sekundær, whiteSpace: 'nowrap' }}>{formatTid(v)}</span>
      ),
    },
  ];

  // Agent-aktivitet til TidslinjeListe format
  const tidslinje = agentAktivitet.slice(0, 12).map(k => ({
    id:    k.id,
    tid:   formatTid(k.oprettet_kl),
    tekst: `${k.fra_agent || '?'} → ${k.til_agent || 'alle'}: ${(k.indhold || '').slice(0, 80)}${(k.indhold || '').length > 80 ? '…' : ''}`,
    farve: STATUS.ai,
  }));

  // Tabs til opgave-sektionen
  const [opgaveTab, setOpgaveTab] = useState('koerer');
  const visteOpgaver = opgaveTab === 'koerer' ? opgaverKoerer : opgaverFejlet;

  return (
    // OS-02: overflowX:hidden på panelets rod-div sikrer at ingen tabel-kolonne
    // sprænger viewport. DataTabel-containere håndterer horisontal scroll internt.
    <div style={{ fontFamily: FONT.family, overflowX: 'hidden' }}>

      {/* Header */}
      <SektionsHeader
        titel="OS Oversigt"
        undertitel="Systemstatus, agentaktivitet og fejllog"
        ikon={<Brain size={16} />}
        badge={erLaast ? 'FEJL' : 'LIVE'}
      />

      {/* System-lås advarsel */}
      {erLaast && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: 'rgba(239,68,68,0.10)',
          border: '1px solid rgba(239,68,68,0.35)',
          borderLeft: `3px solid ${STATUS.fejl}`,
          borderRadius: RADIUS.kort,
          padding: '12px 16px',
          marginBottom: SPACING.gap,
          fontSize: 13,
        }}>
          <AlertCircle size={16} color={STATUS.fejl} style={{ flexShrink: 0 }} />
          <span style={{ color: STATUS.fejl, fontWeight: 700 }}>System er låst (system_laast = true)</span>
          <span style={{ color: TEKST.sekundær, marginLeft: 8 }}>Ingen OS-agenter kører. Lås op via os.tilstand på serveren.</span>
        </div>
      )}

      {/* KPI-rad */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobil ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
        gap: SPACING.gap,
        marginBottom: SPACING.sektionGap,
      }}>
        <KpiKort
          label="Opgaver i gang"
          kortLabel="I GANG"
          value={stat ? igangvaerende : undefined}
          farve={STATUS.ai}
          ikon={<Activity size={16} />}
          badge="LIVE"
          mobil={isMobil}
        />
        <KpiKort
          label="Fejlede 24h"
          kortLabel="FEJL"
          value={stat ? fejlet24h : undefined}
          farve={fejlet24h > 0 ? STATUS.fejl : STATUS.live}
          ikon={<AlertCircle size={16} />}
          badge="LIVE"
          mobil={isMobil}
        />
        <KpiKort
          label="Fuldførte 7d"
          kortLabel="7D"
          value={stat ? fuldfoert7d : undefined}
          farve={STATUS.konfigureret}
          ikon={<CheckCircle2 size={16} />}
          badge="LIVE"
          mobil={isMobil}
        />
        <KpiKort
          label="Afventende"
          kortLabel="AFVENT"
          value={stat ? afventer : undefined}
          farve={afventer > 0 ? STATUS.opmærksomhed : STATUS.live}
          ikon={<Clock size={16} />}
          badge="LIVE"
          mobil={isMobil}
        />
        <KpiKort
          label="System-lås"
          kortLabel="LÅS"
          value={tilstandListe.length === 0 ? undefined : (erLaast ? 'LÅST' : 'OK')}
          farve={erLaast ? STATUS.fejl : STATUS.live}
          ikon={<ShieldCheck size={16} />}
          badge={tilstandListe.length === 0 ? 'IKKE FORBUNDET' : 'LIVE'}
          mobil={isMobil}
        />
      </div>

      {/* Sektion 1: Agentaktivitet (åben) */}
      <SammenklappeligSektion
        titel="Agentaktivitet"
        defaultÅben={true}
        isMobil={isMobil}
        style={{ marginBottom: SPACING.sektionGap }}
      >
        {agentAktivitet.length === 0 ? (
          <TomTilstand
            titel="Ingen agentaktivitet"
            besked="Agent-kommunikation vises her ved næste agent-kørsel."
            ikon={<Brain size={24} />}
            badge="LIVE"
          />
        ) : (
          <TidslinjeListe
            elementer={tidslinje}
            tomBesked="Ingen agentaktivitet endnu"
          />
        )}
      </SammenklappeligSektion>

      {/* Sektion 2: Kørende + fejlede opgaver med tabs (åben) */}
      <SammenklappeligSektion
        titel="Opgaver"
        defaultÅben={true}
        isMobil={isMobil}
        style={{ marginBottom: SPACING.sektionGap }}
      >
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[
            { id: 'koerer', label: `Kørende (${opgaverKoerer.length})` },
            { id: 'fejlet', label: `Fejlede (${opgaverFejlet.length})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setOpgaveTab(t.id)}
              style={{
                background: opgaveTab === t.id
                  ? (t.id === 'fejlet' ? 'rgba(239,68,68,0.15)' : 'rgba(139,92,246,0.15)')
                  : 'rgba(255,255,255,0.04)',
                border: `1px solid ${opgaveTab === t.id
                  ? (t.id === 'fejlet' ? 'rgba(239,68,68,0.35)' : 'rgba(139,92,246,0.35)')
                  : KANT.fin}`,
                borderRadius: RADIUS.knap,
                color: opgaveTab === t.id
                  ? (t.id === 'fejlet' ? STATUS.fejl : STATUS.ai)
                  : TEKST.sekundær,
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                padding: '5px 14px',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {visteOpgaver.length === 0 ? (
          <TomTilstand
            titel={opgaveTab === 'koerer' ? 'Ingen kørende opgaver' : 'Ingen fejlede opgaver'}
            besked={opgaveTab === 'koerer' ? 'OS-systemet er i ro.' : 'Ingen fejl de seneste dage.'}
            ikon={opgaveTab === 'koerer' ? <CheckCircle2 size={24} /> : <AlertCircle size={24} />}
            badge={opgaveTab === 'fejlet' ? 'LIVE' : 'LIVE'}
          />
        ) : isMobil ? (
          <OpgaveKortListe opgaver={visteOpgaver} />
        ) : (
          <DataTabel
            kolonner={opgaveKolonner}
            rækker={visteOpgaver}
            tomBesked="Ingen opgaver"
          />
        )}
      </SammenklappeligSektion>

      {/* Sektion 3: Systemtilstand (lukket som default) */}
      <SammenklappeligSektion
        titel="Systemtilstand"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: SPACING.sektionGap }}
      >
        {tilstandListe.length === 0 ? (
          <TomTilstand
            titel="Tilstand ikke tilgængelig"
            besked="GET /api/os/tilstand returnerede ingen data."
            ikon={<ShieldCheck size={24} />}
            badge="IKKE FORBUNDET"
          />
        ) : isMobil ? (
          <TilstandKortListe tilstand={tilstandListe} />
        ) : (
          <DataTabel
            kolonner={tilstandKolonner}
            rækker={tilstandListe}
            tomBesked="Ingen tilstands-data"
          />
        )}
      </SammenklappeligSektion>

      {/* Sektion 4: Fejllog (lukket som default) */}
      <SammenklappeligSektion
        titel="Fejllog (seneste 50)"
        defaultÅben={false}
        isMobil={isMobil}
      >
        {logLinjer.length === 0 ? (
          <TomTilstand
            titel="Ingen fejl registreret"
            besked="Fejlloggen er tom — systemet kører uden fejl."
            ikon={<CheckCircle2 size={24} />}
            badge="LIVE"
          />
        ) : (
          <DataTabel
            kolonner={fejlKolonner}
            rækker={logLinjer}
            tomBesked="Ingen fejl i loggen"
          />
        )}
      </SammenklappeligSektion>

    </div>
  );
}
